<script setup lang="ts">
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { Badge } from '../ui/badge';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';

const clientVersion = __APP_VERSION__;

const sessionStore = useSessionStore();
const settingsStore = useSettingsStore();

interface ChangelogEntry {
    version: string;
    date: string;
    type: 'New' | 'Improvement' | 'Fix';
    description: string[];
}

const changelogEntries: ChangelogEntry[] = [
    {
        version: 'Client 3.6.0 / App 2.7.1',
        date: '22 Aug 2026',
        type: 'Improvement',
        description: [
            'Profiles now automatically update after a model is no longer available.',
            'Profiles can now be saved individually.',
            'Added support for attaching multiple screenshots during a run.',
            'Added the ability to configure reasoning level.',
        ],
    },
    {
        version: 'Client 3.5.2',
        date: '7 Jan 2026',
        type: 'Fix',
        description: ['Fixed a bug where certain AI models where acting as text-only.'],
    },
    {
        version: 'Client 3.5.1',
        date: '7 Jan 2026',
        type: 'Improvement',
        description: ['Updated the recommended and default models'],
    },
    {
        version: 'Client 3.5.0',
        date: '23 Sep 2025',
        type: 'New',
        description: ['Added the ability to start a session from the web view'],
    },
    {
        version: 'Client 3.4.0',
        date: '7 Aug 2025',
        type: 'New',
        description: ['Added OpenAI GPT-5 support'],
    },
    {
        version: 'Client 3.3.4',
        date: '3 Aug 2025',
        type: 'Fix',
        description: ['Fixed a bug where profile steps would be deleted when moving steps'],
    },
    {
        version: 'Client 3.3.3',
        date: '17 Jul 2025',
        type: 'Improvement',
        description: ['Various minor updates and improvements'],
    },
    {
        version: 'Client 3.3.2',
        date: '5 Apr 2025',
        type: 'Fix',
        description: ['Added a warning when trying to edit default profiles'],
    },
    {
        version: 'Client 3.3.1',
        date: '4 Apr 2025',
        type: 'Improvement',
        description: [
            'Added an option to toggle the visibility of the taskbar/dock icon',
            'Added tooltips to the settings menu',
            'Improved change detection in the edit menu',
        ],
    },
    {
        version: 'Client 3.3.0 / App 2.7.0',
        date: '3 Apr 2025',
        type: 'Improvement',
        description: [
            'Improved crash handling',
            'Improved markdown rendering',
            'Added temperature option (randomness) for prompts',
            'Made it easier to navigate to the edit page from a prompt',
        ],
    },
    {
        version: 'Client 3.2.9',
        date: '3 Apr 2025',
        type: 'Fix',
        description: ['Fixed a bug where arrow keys were not recognized during hotkey rebinding'],
    },
    {
        version: 'Client 3.2.8',
        date: '3 Apr 2025',
        type: 'Improvement',
        description: [
            'Fixed a bug where scrolling was not working in the output page under certain conditions',
            'Improved responsiveness of the settings menu',
        ],
    },
    {
        version: 'Client 3.2.7 / App 2.6.4',
        date: '10 Mar 2025',
        type: 'Fix',
        description: [
            'Fixed a bug where the app could become visible after leaving hidden mode under certain conditions on Windows systems',
        ],
    },
    {
        version: 'Client 3.2.6',
        date: '9 Mar 2025',
        type: 'Fix',
        description: [
            'Fixed a bug where hotkeys were not working for choice selection with multiple output components visible',
        ],
    },
    {
        version: 'Client 3.2.5',
        date: '5 Mar 2025',
        type: 'Improvement',
        description: ['Added the option back to unpin the app from always being on top'],
    },
    {
        version: 'App 2.6.3',
        date: '5 Mar 2025',
        type: 'Improvement',
        description: ['Improved how the app behaven when in hidden mode'],
    },
    {
        version: 'Client 3.2.4',
        date: '3 Mar 2025',
        type: 'Fix',
        description: [
            'Fixed a bug where the taskbar icon was not being hidden on startup under certain conditions',
        ],
    },
    {
        version: 'Client 3.2.3',
        date: '3 Mar 2025',
        type: 'Fix',
        description: [
            "Fixed a bug where the prompt order wasn't being updated properly on prompt deletion",
        ],
    },
    {
        version: 'Client 3.2.2 / App 2.6.2',
        date: '3 Mar 2025',
        type: 'New',
        description: ['Improved default app invisibility'],
    },
    {
        version: 'Client 3.2.1 / App 2.6.1',
        date: '26 Feb 2025',
        type: 'New',
        description: ['Increase default window width', 'Various responsiveness improvements'],
    },
    {
        version: 'Client 3.2.0 / App 2.6.0',
        date: '24 Feb 2025',
        type: 'New',
        description: [
            'Added the the ability to move the app using the hotkeys',
            'Improved the way the app behaves in hidden mode (no longer grabs focus or hides cursor)',
        ],
    },
    {
        version: 'Client 3.1.2',
        date: '22 Feb 2025',
        type: 'Fix',
        description: ['Added cropping to certain image sources to avoid LLM censorship triggers'],
    },
    {
        version: 'Client 3.1.1',
        date: '22 Feb 2025',
        type: 'Fix',
        description: ['Modified the default Leetcode profile to improve reliability'],
    },
    {
        version: 'Client 3.1.0 / App 2.5.0',
        date: '21 Feb 2025',
        type: 'New',
        description: ['Added the ability to switch between profiles using a hotkey'],
    },
    {
        version: 'Client 3.0.1 - 3.0.3',
        date: '15 Feb 2025',
        type: 'Improvement',
        description: ['Various bug fixes related to the large 3.0.0 release'],
    },
    {
        version: '🎉 Client 3.0.0',
        date: '14 Feb 2025',
        type: 'New',
        description: [
            'Our team has worked tirelessly to bring you our biggest update yet, featuring tons of new additions:',
            'Added the ability to create custom prompts, profiles, custom variables and more',
            'Added support for more than 220 new AI models',
            'Drastically improved client performance and stability',
        ],
    },
    {
        version: 'Client 2.4.1',
        date: '4 Dec 2024',
        type: 'Improvement',
        description: ['Added the ability to hide the OpenAI o1 banner'],
    },
    {
        version: 'Client 2.4.0 / App 2.4.0',
        date: '1 Dec 2024',
        type: 'New',
        description: ['Added the ability to switch between languages using hotkeys'],
    },
    {
        version: 'Client 2.3.1 / App 2.3.2',
        date: '7 Nov 2024',
        type: 'Improvement',
        description: ['Improved error handling'],
    },
    {
        version: 'Client 2.3.0',
        date: '6 Nov 2024',
        type: 'New',
        description: ['Added beta support for OpenAI o1-mini'],
    },
    {
        version: 'Client 2.2.2',
        date: '4 Nov 2024',
        type: 'Improvement',
        description: ['Removed all cursor hover states'],
    },
    {
        version: 'App 2.3.1',
        date: '23 Oct 2024',
        type: 'Fix',
        description: ['Fix possible app crash occurring during solve setup on MacOS'],
    },
    {
        version: 'App 2.3.0',
        date: '10 Oct 2024',
        type: 'New',
        description: [
            'Updated to latest Electron version (v32)',
            'Added a warning notification when hotkeys fail to register',
        ],
    },
    {
        version: 'Client 2.2.1',
        date: '9 Oct 2024',
        type: 'New',
        description: [
            'Fixed a bug where the output panels would sometimes show markdown fragments',
            'Fixed a bug where the web view connection bar would be empty',
            'Fixed a bug where the login submit button would disappear under certain conditions',
        ],
    },
    {
        version: 'Client 2.2.0',
        date: '30 Sep 2024',
        type: 'New',
        description: [
            'Added the ability to switch AI models (Options -> Configure AI model)',
            'Added support for Claude 3.5 Sonnet',
            'Finally started a changelog 😀',
        ],
    },
];

const updateLastSeenChangelogVersion = () => {
    setTimeout(() => {
        settingsStore.lastSeenChangelogVersion = clientVersion;
    }, 150);
};
</script>

<template>
    <HoverCard>
        <HoverCardTrigger
            class="flex items-center text-xs"
            @mouseenter="updateLastSeenChangelogVersion"
        >
            <span v-if="sessionStore.desktopAppVersion && clientVersion">
                {{ `${sessionStore.desktopAppVersion}` }} |
                {{ `${clientVersion}` }}
            </span>
            <span v-else-if="clientVersion">
                {{ `${clientVersion}` }}
            </span>
            <div
                class="changelog-dot"
                v-if="settingsStore.lastSeenChangelogVersion !== clientVersion"
            ></div>
        </HoverCardTrigger>
        <HoverCardContent class="w-[50vh] text-xs">
            <h3 class="mb-3 text-base font-medium">Changelog</h3>
            <div class="custom-scrollbar max-h-[200px] overflow-y-auto overflow-x-hidden">
                <div class="changelog-entry" v-for="entry in changelogEntries" :key="entry.version">
                    {{ entry.version }} ({{ entry.date }})
                    <Badge
                        :class="{
                            'bg-green-600': entry.type === 'New',
                            'bg-blue-600': entry.type === 'Improvement',
                            'bg-orange-600': entry.type === 'Fix',
                        }"
                        class="ms-1 px-1 py-0 font-normal text-gray-100"
                    >
                        {{ entry.type }}
                    </Badge>
                    <ul class="ml-4 mt-1 list-disc">
                        <li v-for="desc in entry.description" :key="desc">{{ desc }}</li>
                    </ul>
                </div>
            </div>
            <div class="mt-4 font-semibold">
                <template v-if="sessionStore.desktopAppVersion">
                    <br />
                    Electron app version: {{ sessionStore.desktopAppVersion }}<br />
                </template>
                Client version: {{ clientVersion }}
            </div>
        </HoverCardContent>
    </HoverCard>
</template>

<style>
.changelog-dot {
    @apply ms-1.5 h-1.5 w-1.5 bg-green-500;
    border-radius: 50%;
    display: inline-block;
}

.changelog-entry:not(:last-child) {
    @apply mb-3;
}
</style>
